/**
 * `/verdicts/:verdictId` — read the regulated verdict a specialist handed back (TASK-703).
 *
 * `compliance` and `risk` produce a verdict, not a document, so before this there was no
 * artifact type they could attach and no surface on which an advisor could read one. Signing
 * off on a source-of-wealth review means reading what the engine decided and why.
 *
 * Everything here is the ENGINE's output. The page names the engine, because "who decided
 * this" is the first thing a reviewer of a regulated record needs to know — and the answer is
 * never "the model".
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Cpu } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { verdictApiService } from '../services/api/verdictApiService';
import { WidgetErrorState } from '../components/ui/WidgetErrorState';

/** Outcomes the two engines produce. Anything else renders neutrally rather than guessing. */
const CLEAN = new Set(['aligned', 'validated', 'compliant']);

const TYPE_LABELS: Record<string, string> = {
  suitability_assessment: 'Suitability assessment',
  compliance_review: 'Compliance review',
};

function List({ title, items }: { title: string; items: unknown[] }) {
  if (!items?.length) return null;
  return (
    <section className="mt-4">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h2>
      <ul className="mt-1 list-disc pl-5 text-sm text-foreground">
        {items.map((item, i) => (
          <li key={i}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>
        ))}
      </ul>
    </section>
  );
}

export default function VerdictPage() {
  const { verdictId } = useParams<{ verdictId: string }>();
  const query = useQuery({
    queryKey: ['regulated-verdict', verdictId],
    queryFn: () => verdictApiService.getVerdict(verdictId as string),
    enabled: Boolean(verdictId),
  });

  if (query.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading the verdict…</div>;
  }
  if (query.isError || !query.data) {
    // Never an empty page: a reviewer who cannot read the verdict must be told so, not shown
    // a blank pane they might read as "nothing was found".
    return (
      <div className="mx-auto max-w-3xl p-6">
        <WidgetErrorState
          title="Couldn't load this verdict"
          detail="The regulated verdict could not be read. Do not sign off on it until you can."
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const v = query.data;
  const clean = CLEAN.has((v.outcome || '').toLowerCase());

  return (
    <div className="mx-auto max-w-3xl p-6" data-testid="regulated-verdict">
      <h1 className="text-lg font-semibold text-foreground">
        {TYPE_LABELS[v.verdict_type] ?? v.verdict_type}
      </h1>

      <div
        data-testid="verdict-outcome"
        className={`mt-3 flex items-start gap-2 rounded border p-3 text-sm ${
          clean
            ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
            : 'border-amber-200 bg-amber-50 text-amber-900'
        }`}
      >
        {clean ? (
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        ) : (
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        )}
        <span>
          <strong>{v.outcome}</strong>
        </span>
      </div>

      <p
        className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground"
        data-testid="verdict-engine"
      >
        <Cpu className="h-3 w-3" aria-hidden="true" />
        Decided by <strong className="font-medium">{v.engine}</strong>
        {v.engine_version ? ` (${v.engine_version})` : ''}
        {v.created_at ? ` · ${new Date(v.created_at).toLocaleString()}` : ''}
      </p>

      {v.supersedes_id && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="verdict-supersedes">
          This verdict corrects an earlier one. Both records are kept.
        </p>
      )}

      {/* TASK-931: the other direction. A suitability verdict is a statement about a risk
          profile at a moment; when the profile is completed again this verdict does not become
          wrong, it becomes about something else — and a page that goes on showing a green
          `pass` is asserting an assessment of a profile the client has already replaced.
          Rendered ABOVE the reasons, because it changes how every line below it should be read. */}
      {v.superseded_by && (
        <div
          className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3"
          data-testid="verdict-superseded-by"
        >
          <p className="text-xs font-medium text-amber-900">{v.superseded_by.label}</p>
          <p className="mt-1 text-[11px] text-amber-800">
            This verdict was issued against an earlier version of the client&rsquo;s risk
            profile. It is kept exactly as issued; it no longer describes the current profile.
            {v.superseded_by.at
              ? ` Superseded ${new Date(v.superseded_by.at).toLocaleString()}.`
              : ''}
          </p>
        </div>
      )}

      <List title="Reasons" items={v.reasons} />
      <List title="Failed checks" items={v.failures} />
      <List title="Checks run" items={v.checks} />
    </div>
  );
}
